import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { pxToRem } from '../../../assets/styles/helper';
import Modal from '../../Modal/Modal';
import { SFH_WIZARD } from '../../../constants/sessionStorage';
import {
  closeWizardContent,
  getWizardContent,
  setWizardContent,
} from '../../../util/helpers/wizardHelpers';
import SellFromHomeProductSelection from './SellFromHomeProductSelection';
import SellFromHomeQuantity from './SellFromHomeQuantity';
import SellFromHomeShipping from './SellFromHomeShipping';
import SellFromHomeStepsDrawer from './SellFromHomeStepsDrawer';

const PRODUCT_SELECTION_STEP = 0;
const QUANTITY_STEP = 1;
const SHIPPING_STEP = 2;

const SellFromHomeWizard = ({ isOpen, onClose, fromSellWizard }) => {
  const { t } = useTranslation();
  const [isStepsDrawerOpen, setIsStepsDrawerOpen] = useState(false);
  const [selectedHit, setSelectedHit] = useState(null);
  const [currentStep, setCurrentStep] = useState(
    () => Number(getWizardContent(SFH_WIZARD)) || PRODUCT_SELECTION_STEP,
  );

  useEffect(() => {
    setIsStepsDrawerOpen(isOpen);
    if (!isOpen) {
      setSelectedHit(null);
      setCurrentStep(PRODUCT_SELECTION_STEP);
      closeWizardContent();
    }
  }, [isOpen]);

  const goToStep = (step) => {
    setWizardContent(SFH_WIZARD, step);
    setCurrentStep(step);
  };

  const goStepBack = () => goToStep(Math.max(currentStep - 1, 0));
  const goStepForward = () =>
    goToStep(Math.min(currentStep + 1, SHIPPING_STEP));

  const handleClose = () => {
    closeWizardContent();
    setSelectedHit(null);
    setCurrentStep(PRODUCT_SELECTION_STEP);
    onClose();
  };

  const handleHeaderBack = () => {
    if (currentStep === PRODUCT_SELECTION_STEP) {
      handleClose();
      return;
    }
    goStepBack();
  };

  const showHeaderBack =
    currentStep !== PRODUCT_SELECTION_STEP ||
    (currentStep === PRODUCT_SELECTION_STEP && fromSellWizard);

  return (
    <Modal
      isOpen={isOpen}
      close={handleClose}
      goBack={showHeaderBack ? handleHeaderBack : undefined}
      title={t('sellWizard.sfhWizard.title')}
      size="sm"
      maxWidth={pxToRem(500)}
      minHeight={pxToRem(576)}
      clipContent
      closeIconVisible
      skipListeningToLocations
    >
      {currentStep === PRODUCT_SELECTION_STEP && (
        <SellFromHomeProductSelection
          selectedHit={selectedHit}
          setSelectedHit={setSelectedHit}
          handleNext={goStepForward}
        />
      )}
      {currentStep === QUANTITY_STEP && (
        <SellFromHomeQuantity
          selectedHit={selectedHit}
          handleBack={goStepBack}
          handleNext={goStepForward}
          handleClose={handleClose}
        />
      )}
      {currentStep === SHIPPING_STEP && (
        <SellFromHomeShipping
          selectedHit={selectedHit}
          handleBack={goStepBack}
          handleNext={goStepForward}
          handleClose={handleClose}
        />
      )}
      <SellFromHomeStepsDrawer
        isOpen={isStepsDrawerOpen}
        onClose={() => setIsStepsDrawerOpen(false)}
      />
    </Modal>
  );
};

SellFromHomeWizard.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  fromSellWizard: PropTypes.bool,
};

export default SellFromHomeWizard;
